import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPenToSquare,
    faFileLines,
    faFileCircleCheck,
    faAddressCard,
    faThumbTack,
    faLocationDot,
    faTruckMoving,
    faClock,
    faPersonRunning,
    faCalendarDays,
    faArrowRight,
    faChevronDown,
} from '@fortawesome/free-solid-svg-icons';

import useReveal from '../Components/useReveal';
import '../Components/styles.scss';
import './styles.scss';

// ── Scroll progress hook (offset-based, for sticky/pinned sections) ─
function useScrollProgress(ref) {
    const [progress, setProgress] = useState(0);
    useEffect(() => {
        if (!ref.current) return undefined;
        let raf = 0;
        const update = () => {
            const el = ref.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight || 1;
            const total = Math.max(1, el.offsetHeight - vh);
            const p = Math.max(0, Math.min(1, -rect.top / total));
            setProgress(p);
        };
        const handler = () => {
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                update();
            });
        };
        update();
        window.addEventListener('scroll', handler, { passive: true });
        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('scroll', handler);
            window.removeEventListener('resize', handler);
            if (raf) window.cancelAnimationFrame(raf);
        };
    }, [ref]);
    return progress;
}

// ── View progress (center-based, fires as section moves through viewport) ─
// 0 = section top entering viewport bottom · 0.5 = centered · 1 = section bottom leaving viewport top
function useViewProgress(ref) {
    const [progress, setProgress] = useState(0);
    useEffect(() => {
        if (!ref.current) return undefined;
        let raf = 0;
        const update = () => {
            const el = ref.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight || 1;
            const sectionCenter = rect.top + rect.height / 2;
            const viewportCenter = vh / 2;
            const total = (rect.height + vh) / 2;
            const dist = sectionCenter - viewportCenter;
            const p = Math.max(0, Math.min(1, 0.5 - dist / (2 * total)));
            setProgress(p);
        };
        const handler = () => {
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                update();
            });
        };
        update();
        window.addEventListener('scroll', handler, { passive: true });
        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('scroll', handler);
            window.removeEventListener('resize', handler);
            if (raf) window.cancelAnimationFrame(raf);
        };
    }, [ref]);
    return progress;
}

// ── Hero section with extreme parallax ───────────────────────
function FeaturesHero() {
    const ref = useRef(null);
    const progress = useScrollProgress(ref);

    const phrases = [
        'Every tool your jobsite needs.',
        'Drawings, RFIs, punch — all here.',
        'Field-first. Offline-ready.',
        'Built for Canadian builders.',
    ];
    const [phraseIdx, setPhraseIdx] = useState(0);
    const [fading, setFading] = useState(false);

    useEffect(() => {
        const id = setInterval(() => {
            setFading(true);
            setTimeout(() => {
                setPhraseIdx((p) => (p + 1) % phrases.length);
                setFading(false);
            }, 400);
        }, 3600);
        return () => clearInterval(id);
    }, [phrases.length]);

    // Parallax computations
    const heroFade = Math.max(0, 1 - progress * 1.4);
    const heroLift = -progress * 90;
    const heroScale = 1 - progress * 0.06;

    // Floating shape transforms — many layers
    const s1 = { y: progress * -340, rot: progress * 90 };
    const s2 = { y: progress * 280, rot: progress * -120 };
    const s3 = { y: progress * -480, x: progress * 200 };
    const s4 = { y: progress * 360, x: progress * -180 };
    const s5 = { y: progress * -620, rot: progress * 220 };
    const s6 = { y: progress * 420, x: progress * 240 };
    const s7 = { y: progress * -260, x: progress * -120 };
    const s8 = { y: progress * 220, rot: progress * -60 };

    const glowA = { y: progress * -260, x: progress * 100 };
    const glowB = { y: progress * 220, x: progress * -140 };
    const glowC = { y: progress * -180 };

    const gridShift = progress * -160;

    return (
        <section ref={ref} className="plf-hero">
            <div className="plf-hero__bg" aria-hidden="true">
                <div
                    className="plf-hero__grid"
                    style={{ transform: `translate3d(0, ${gridShift}px, 0)` }}
                />
                <span
                    className="plf-hero__glow plf-hero__glow--orange"
                    style={{ transform: `translate3d(${glowA.x}px, ${glowA.y}px, 0)` }}
                />
                <span
                    className="plf-hero__glow plf-hero__glow--blue"
                    style={{ transform: `translate3d(${glowB.x}px, ${glowB.y}px, 0)` }}
                />
                <span
                    className="plf-hero__glow plf-hero__glow--purple"
                    style={{ transform: `translate3d(0, ${glowC.y}px, 0)` }}
                />

                {/* Floating feature-icon "constellation" — multi-depth parallax */}
                <span
                    className="plf-shape plf-shape--ring-lg"
                    style={{ transform: `translate3d(0, ${s1.y}px, 0) rotate(${s1.rot}deg)` }}
                />
                <span
                    className="plf-shape plf-shape--square"
                    style={{ transform: `translate3d(0, ${s2.y}px, 0) rotate(${s2.rot}deg)` }}
                />
                <span
                    className="plf-shape plf-shape--triangle"
                    style={{ transform: `translate3d(${s3.x}px, ${s3.y}px, 0)` }}
                />
                <span
                    className="plf-shape plf-shape--ring-sm"
                    style={{ transform: `translate3d(${s4.x}px, ${s4.y}px, 0)` }}
                />
                <span
                    className="plf-shape plf-shape--diamond"
                    style={{ transform: `translate3d(0, ${s5.y}px, 0) rotate(${s5.rot + 45}deg)` }}
                />
                <span
                    className="plf-shape plf-shape--dot plf-shape--dot-a"
                    style={{ transform: `translate3d(${s6.x}px, ${s6.y}px, 0)` }}
                />
                <span
                    className="plf-shape plf-shape--dot plf-shape--dot-b"
                    style={{ transform: `translate3d(${s7.x}px, ${s7.y}px, 0)` }}
                />
                <span
                    className="plf-shape plf-shape--cross"
                    style={{ transform: `translate3d(0, ${s8.y}px, 0) rotate(${s8.rot}deg)` }}
                />
                <span
                    className="plf-shape plf-shape--blur-orb"
                    style={{ transform: `translate3d(${-s3.x * 0.6}px, ${s3.y * 0.5}px, 0)` }}
                />
            </div>

            <div className="plf-hero__sticky">
                <div
                    className="plf-hero__inner"
                    style={{
                        opacity: heroFade,
                        transform: `translate3d(0, ${heroLift}px, 0) scale(${heroScale})`,
                    }}
                >
                    <span className="plf-hero__pill">
                        <span className="plf-hero__pill-dot" /> 11 features. 1 platform.
                    </span>
                    <h1 className="plf-hero__title">
                        <span className={`plf-hero__phrase${fading ? ' is-fading' : ''}`}>
                            {phrases[phraseIdx]}
                        </span>
                    </h1>
                    <p className="plf-hero__lede">
                        ProjectLoop is a complete construction platform — drawings, RFIs, daily logs, punch
                        lists, schedule, forms, locations, and more. Every workflow built for the field,
                        every screen built for speed.
                    </p>
                    <div className="plf-hero__actions">
                        <Link to="/signup" className="plf-btn plf-btn--primary">
                            Start 90-day free trial <FontAwesomeIcon icon={faArrowRight} />
                        </Link>
                        <Link to="/pricing" className="plf-btn plf-btn--ghost">
                            See pricing
                        </Link>
                    </div>

                    <div className="plf-hero__stats">
                        <div>
                            <strong>10+</strong>
                            <span>Field tools, one app</span>
                        </div>
                        <div>
                            <strong>Offline</strong>
                            <span>Ready at the slab edge</span>
                        </div>
                        <div>
                            <strong>0</strong>
                            <span>Sales calls required</span>
                        </div>
                        <div>
                            <strong>90d</strong>
                            <span>Free trial</span>
                        </div>
                    </div>
                </div>

                <span className="plf-hero__scroll-cue" aria-hidden="true">
                    <span>Scroll</span>
                    <FontAwesomeIcon icon={faChevronDown} />
                </span>
            </div>
        </section>
    );
}

// ── Feature spotlight (view-progress driven; smooth + early fade-in) ─
function FeatureSpotlight({ index, eyebrow, title, body, points, accent, illustration }) {
    const ref = useRef(null);
    const progress = useViewProgress(ref);

    // Fade-in completes by progress 0.32 (well before section centers at 0.5)
    // Stays at 1 through the middle, gently fades out near the end (0.85 → 1)
    const fadeIn =
        progress < 0.32
            ? Math.max(0, (progress - 0.05) / 0.27)
            : progress > 0.85
                ? Math.max(0, 1 - (progress - 0.85) / 0.15)
                : 1;

    // Initial lift settles to 0 by progress 0.32
    const lift = progress < 0.32 ? Math.max(0, (0.32 - progress) * 220) : 0;

    // Illustration parallax — runs across the whole visible range
    const illuScale = 0.88 + Math.min(1, progress * 1.4) * 0.14;
    const illuRot = (progress - 0.5) * 6;
    const illuY = (0.5 - progress) * 60;

    const isReversed = index % 2 === 1;

    return (
        <section
            ref={ref}
            className={`plf-spotlight plf-spotlight--${accent}${isReversed ? ' plf-spotlight--reverse' : ''}`}
        >
            <div className="plf-spotlight__bg" aria-hidden="true">
                <span
                    className="plf-spotlight__glow"
                    style={{ transform: `translate3d(0, ${progress * -180}px, 0)` }}
                />
                <span
                    className="plf-spotlight__shape plf-spotlight__shape--a"
                    style={{ transform: `translate3d(${progress * 220}px, ${progress * -140}px, 0) rotate(${progress * 60}deg)` }}
                />
                <span
                    className="plf-spotlight__shape plf-spotlight__shape--b"
                    style={{ transform: `translate3d(${progress * -180}px, ${progress * 200}px, 0)` }}
                />
            </div>
            <div className="plf-spotlight__inner">
                <div
                    className="plf-spotlight__copy"
                    style={{ opacity: fadeIn, transform: `translate3d(0, ${lift}px, 0)` }}
                >
                    <span className="plf-spotlight__num">{String(index + 1).padStart(2, '0')}</span>
                    <p className="plf-spotlight__eyebrow">{eyebrow}</p>
                    <h2>{title}</h2>
                    <p className="plf-spotlight__body">{body}</p>
                    <ul>
                        {points.map((pt) => (
                            <li key={pt}>
                                <span className="plf-spotlight__check">✓</span> {pt}
                            </li>
                        ))}
                    </ul>
                </div>
                <div
                    className="plf-spotlight__visual"
                    style={{
                        transform: `translate3d(0, ${illuY}px, 0) scale(${illuScale}) rotate(${illuRot}deg)`,
                    }}
                >
                    {illustration}
                </div>
            </div>
        </section>
    );
}

// ── Mock illustrations ──────────────────────────────────────
function DrawingsIllustration() {
    return (
        <div className="plf-illu plf-illu--drawings">
            <div className="plf-illu__frame">
                <div className="plf-illu__topbar">
                    <span>A-12.19 · Roof Slab Edge</span>
                    <span className="plf-illu__pill">R3 Current</span>
                </div>
                <div className="plf-illu__canvas">
                    <div className="plf-illu__grid" />
                    <div className="plf-illu__shape plf-illu__shape--a" />
                    <div className="plf-illu__shape plf-illu__shape--b" />
                    <div className="plf-illu__shape plf-illu__shape--c" />
                    <div className="plf-illu__hl plf-illu__hl--y" />
                    <div className="plf-illu__hl plf-illu__hl--r" />
                    <div className="plf-illu__pin plf-illu__pin--a" />
                    <div className="plf-illu__pin plf-illu__pin--b" />
                </div>
                <div className="plf-illu__toolbar">
                    <span /><span /><span /><span /><span />
                </div>
            </div>
            <div className="plf-illu__chip plf-illu__chip--a">Synced</div>
            <div className="plf-illu__chip plf-illu__chip--b">v3 of 8</div>
        </div>
    );
}

function RfiIllustration() {
    return (
        <div className="plf-illu plf-illu--rfi">
            <div className="plf-illu__rfi-card">
                <div className="plf-illu__rfi-head">
                    <span className="plf-illu__rfi-id">#12</span>
                    <span className="plf-illu__rfi-status">Open</span>
                </div>
                <h4>InSinkErator clearance issue</h4>
                <p>
                    Original specs do not fit under the sink. Two proposed solutions
                    submitted for review.
                </p>
                <div className="plf-illu__rfi-thread">
                    <div className="plf-illu__rfi-msg">
                        <div className="plf-illu__avatar plf-illu__avatar--orange">CH</div>
                        <div>
                            <strong>Courtney H.</strong>
                            <span>Submitted question · 2h</span>
                        </div>
                    </div>
                    <div className="plf-illu__rfi-msg">
                        <div className="plf-illu__avatar plf-illu__avatar--blue">LA</div>
                        <div>
                            <strong>Leslie A.</strong>
                            <span>Replied · just now</span>
                        </div>
                    </div>
                </div>
                <div className="plf-illu__rfi-cta">Set Official Response →</div>
            </div>
            <div className="plf-illu__chip plf-illu__chip--a">12 RFIs · 9 closed</div>
        </div>
    );
}

function PunchIllustration() {
    return (
        <div className="plf-illu plf-illu--punch">
            <div className="plf-illu__punch-card">
                <div className="plf-illu__punch-head">
                    <h4>Internal Piping Fixes</h4>
                    <span className="plf-illu__pill plf-illu__pill--green">In Progress</span>
                </div>
                {[
                    ['1.1', 'Hazard instructions written?', 'pass'],
                    ['1.2', 'Emergency shutdown documented?', 'pass'],
                    ['1.3', 'All staff briefed on hazards?', 'na'],
                    ['1.4', 'Manufacturer instructions distributed?', 'pass'],
                ].map(([id, q, st]) => (
                    <div key={id} className="plf-illu__punch-row">
                        <span className="plf-illu__punch-id">#{id}</span>
                        <span className="plf-illu__punch-q">{q}</span>
                        <div className="plf-illu__punch-btns">
                            <span className={`plf-illu__punch-btn${st === 'pass' ? ' is-pass' : ''}`}>P</span>
                            <span className="plf-illu__punch-btn">F</span>
                            <span className={`plf-illu__punch-btn${st === 'na' ? ' is-na' : ''}`}>N/A</span>
                        </div>
                    </div>
                ))}
            </div>
            <div className="plf-illu__chip plf-illu__chip--a">3 of 4 pass</div>
        </div>
    );
}

function DailyLogIllustration() {
    return (
        <div className="plf-illu plf-illu--daily">
            <div className="plf-illu__daily-card">
                <h4>This week</h4>
                {[
                    ['Wed Mar 27', 'Today', 'today'],
                    ['Tue Mar 26', 'Pending', 'pending'],
                    ['Mon Mar 25', 'Completed', 'done'],
                    ['Sat Mar 23', 'Completed', 'done'],
                ].map(([day, label, st]) => (
                    <div key={day} className={`plf-illu__daily-row plf-illu__daily-row--${st}`}>
                        <strong>{day}</strong>
                        <span>{label}</span>
                    </div>
                ))}
            </div>
            <div className="plf-illu__chip plf-illu__chip--a">Auto · Weather · Crews</div>
        </div>
    );
}

// ── All-features grid ──────────────────────────────────────
const allFeatures = [
    {
        url: '/features/drawings',
        icon: faPenToSquare,
        name: 'Drawings',
        body: 'Version-controlled plans, in-field markup, and a single source of truth — works offline at the slab edge.',
    },
    {
        url: '/features/rfi',
        icon: faFileLines,
        name: 'RFIs',
        body: 'Track every question, route to the right reviewer, and close the loop with a stamped audit trail.',
    },
    {
        url: '/features/submittals',
        icon: faFileCircleCheck,
        name: 'Submittals',
        body: 'Submit, review, approve, and stamp materials and shop drawings — with deadline tracking baked in.',
    },
    {
        url: '/features/daily_log',
        icon: faAddressCard,
        name: 'Daily Logs',
        body: 'Weather, crews, hours, photos, and progress — captured in 2 minutes per day from any device.',
    },
    {
        url: '/features/punchlists',
        icon: faThumbTack,
        name: 'Punch Lists',
        body: 'Move projects to closeout faster with field-first deficiency tracking and automatic reviewer routing.',
    },
    {
        url: '/features/schedule',
        icon: faCalendarDays,
        name: 'Schedule',
        body: 'Real-time critical-path visibility, milestone tracking, and live progress updates that roll into the master schedule the moment they happen.',
    },
    {
        url: '/features/locations',
        icon: faLocationDot,
        name: 'Locations',
        body: 'QR-code every building, floor, room, and zone, then tie every form, inspection, deficiency, and action item to an exact spot. Scan and instantly see what\'s happening — and what needs attention — right where you\'re standing.',
    },
    {
        url: '/features/asset_tracking',
        icon: faTruckMoving,
        name: 'Asset Tracking',
        body: 'Track tools, equipment, and inventory across every project — assignments, locations, and maintenance.',
    },
    {
        url: '/features/time_tracking',
        icon: faClock,
        name: 'Time Tracking',
        body: 'Crew clock-in, hour reconciliation, and labor analytics that match your project budget line items.',
    },
    {
        url: '/features/actions',
        icon: faPersonRunning,
        name: 'Action Items',
        body: 'Assign, remind, and resolve every task with full accountability — from RFIs to safety to punch.',
    },
    {
        url: '/features/forms',
        icon: faFileLines,
        name: 'Forms',
        body: 'Build any form, any inspection, any checklist — and capture it digitally on every device.',
    },
];

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
    const [ref, revealed] = useReveal();
    const cls = `plf-reveal${revealed ? ' is-revealed' : ''}${className ? ` ${className}` : ''}`;
    return (
        <Tag
            ref={ref}
            className={cls}
            style={delay ? { transitionDelay: `${delay}ms` } : undefined}
            {...rest}
        >
            {children}
        </Tag>
    );
}

// ── Page ────────────────────────────────────────────────────
export default function FeaturesHomePage() {
    const spotlights = [
        {
            eyebrow: 'Drawings',
            title: 'Drawings, version control, and markup — all on the slab.',
            body: "Every revision tracked, every annotation synced, every set always current. Drawings cached on device so your foreman doesn't lose 30 minutes to a missing plan.",
            points: [
                'Auto-retire old revisions when new sheets land',
                'In-field highlight, markup, and notes',
                'Offline mode for poor-connectivity sites',
                'Audit trail of every annotation and viewer',
            ],
            accent: 'orange',
            illustration: <DrawingsIllustration />,
        },
        {
            eyebrow: 'RFIs',
            title: 'Every question, every answer, in one stamped thread.',
            body: 'Stop chasing email replies. RFIs route to the right reviewer instantly, and every response is logged with timestamp, role, and stamp — ready for the closeout binder.',
            points: [
                'Auto-route by trade, role, or project area',
                'Public + private threads',
                'Stamps and signatures inline',
                'Owner-ready audit trail on demand',
            ],
            accent: 'blue',
            illustration: <RfiIllustration />,
        },
        {
            eyebrow: 'Punch Lists',
            title: 'Move projects to closeout faster.',
            body: "Crews log deficiencies on the spot. Automatic reviewer assignment. Photo-before-photo-after on every item. Closeout that doesn't take a month.",
            points: [
                'Mobile-first deficiency capture',
                'Auto-assigns reviewers by area',
                'Photo proof on every line',
                'Owner sign-off in two taps',
            ],
            accent: 'green',
            illustration: <PunchIllustration />,
        },
        {
            eyebrow: 'Daily Logs',
            title: 'Two minutes a day captures everything.',
            body: 'Weather, temperature, crews, hours, equipment, photos, and progress notes — captured automatically when possible, with voice-to-text for the rest. End-of-day paperwork, gone.',
            points: [
                'Auto-pulled weather and conditions',
                'Voice-to-text dictation built in',
                'Photo timeline of every job',
                'One-click PDF export for owners',
            ],
            accent: 'purple',
            illustration: <DailyLogIllustration />,
        },
    ];

    return (
        <div className="plf-page">
            <FeaturesHero />

            {/* Capability pillars */}
            <section className="plf-pillars">
                <Reveal className="plf-pillars__lead">
                    <p className="plf-eyebrow">What's inside</p>
                    <h2>Three pillars. Eleven tools. One platform.</h2>
                    <p className="plf-pillars__sub">
                        Every workflow your jobsite runs on, organized into the three things construction
                        is really about.
                    </p>
                </Reveal>
                <div className="plf-pillars__grid">
                    <Reveal className="plf-pillar plf-pillar--field">
                        <span className="plf-pillar__chip">Pillar 01</span>
                        <h3>Field-first capture</h3>
                        <p>
                            Daily Logs, Punch, Forms, and Time Tracking — designed for the slab, the
                            scaffold, and the trailer. Mobile-first, offline-ready.
                        </p>
                        <ul>
                            <li>Daily Logs</li>
                            <li>Punch Lists</li>
                            <li>Forms</li>
                            <li>Time Tracking</li>
                        </ul>
                    </Reveal>
                    <Reveal className="plf-pillar plf-pillar--visual" delay={120}>
                        <span className="plf-pillar__chip">Pillar 02</span>
                        <h3>Visual truth</h3>
                        <p>
                            Drawings, Locations, and Asset Tracking — keep every plan, place, and tool
                            visible to the people who need it, when they need it.
                        </p>
                        <ul>
                            <li>Drawings</li>
                            <li>Locations</li>
                            <li>Asset Tracking</li>
                        </ul>
                    </Reveal>
                    <Reveal className="plf-pillar plf-pillar--sync" delay={240}>
                        <span className="plf-pillar__chip">Pillar 03</span>
                        <h3>Team sync</h3>
                        <p>
                            RFIs, Submittals, Schedule, and Action Items — every question, milestone, and
                            to-do tracked in one place with a stamped paper trail.
                        </p>
                        <ul>
                            <li>RFIs</li>
                            <li>Submittals</li>
                            <li>Schedule</li>
                            <li>Action Items</li>
                        </ul>
                    </Reveal>
                </div>
            </section>

            {/* Feature spotlights */}
            {spotlights.map((sp, idx) => (
                <FeatureSpotlight key={sp.eyebrow} index={idx} {...sp} />
            ))}

            {/* All features grid */}
            <section className="plf-grid-section">
                <Reveal className="plf-grid-section__lead">
                    <p className="plf-eyebrow">All features</p>
                    <h2>The complete toolkit.</h2>
                    <p>Every workflow construction needs — built once, included on every plan.</p>
                </Reveal>
                <div className="plf-grid">
                    {allFeatures.map((f, idx) => (
                        <Reveal key={f.name} delay={(idx % 3) * 80}>
                            <Link to={f.url} className="plf-grid__card">
                                <span className="plf-grid__icon">
                                    <FontAwesomeIcon icon={f.icon} />
                                </span>
                                <h3>{f.name}</h3>
                                <p>{f.body}</p>
                                <span className="plf-grid__cta">
                                    Learn more <FontAwesomeIcon icon={faArrowRight} />
                                </span>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="plf-cta">
                <Reveal className="plf-cta__card">
                    <p className="plf-eyebrow">Ready to try it?</p>
                    <h2>90 days free. No card. No commitment.</h2>
                    <p>
                        Run a real project on ProjectLoop with your real crews. If we don't pay for
                        ourselves before day 90, walk away.
                    </p>
                    <div className="plf-cta__actions">
                        <Link to="/signup" className="plf-btn plf-btn--primary">
                            Start free trial <FontAwesomeIcon icon={faArrowRight} />
                        </Link>
                        <Link to="/contactus" className="plf-btn plf-btn--ghost">
                            Talk to a builder
                        </Link>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
